import { ACTIVATION_LABELS, resolveSupportLabel } from '../classification';
import { INFRASTRUCTURE_LABELS } from './buildCheckboxFilterOptions';
import type { ExplorationFilter } from '../../types/exploration';
import type { CapacityBounds } from '../../components/filters/utils/capacityFormat';
import { formatCapacityRangeBadge } from '../../components/filters/utils/capacityFormat';
import { traceExplorationFilterButton } from './explorationFilterButtonTrace';
import { explorationFiltersEqual } from './removeExplorationFilter';

export interface FilterButtonTag {
  filter: ExplorationFilter;
  label: string;
  /** Long values truncate inside the chip. */
  truncate?: boolean;
}

export interface FilterButtonGroup {
  key: string;
  label: string;
  tags: FilterButtonTag[];
}

/** Horizontal gap between filter button sections (Tailwind gap-2). */
export const filterButtonRowGapPx = 8;

export interface FitFilterButtonGroupsInput {
  fullGroups: FilterButtonGroup[];
  filters: ExplorationFilter[];
  triggerWidth: number;
  /** Width of group row with label + first N tags (index N-1). */
  groupPartialWidths: Record<string, number[]>;
  otherChipWidthForCount: (count: number) => number;
  availableWidth: number;
  gapPx?: number;
}

interface FlatFilterTag {
  groupKey: string;
  groupLabel: string;
  tag: FilterButtonTag;
  tagIndexInGroup: number;
}

function flattenPrimaryGroupTags(fullGroups: FilterButtonGroup[]): FlatFilterTag[] {
  const items: FlatFilterTag[] = [];
  fullGroups
    .filter((group) => group.key !== 'other')
    .forEach((group) => {
      group.tags.forEach((tag, tagIndexInGroup) => {
        items.push({
          groupKey: group.key,
          groupLabel: group.label,
          tag,
          tagIndexInGroup,
        });
      });
    });
  return items;
}

function primaryGroupOrder(fullGroups: FilterButtonGroup[]): string[] {
  return fullGroups.filter((group) => group.key !== 'other').map((group) => group.key);
}

function visibleFiltersFromCounts(
  fullGroups: FilterButtonGroup[],
  visibleCounts: Map<string, number>
): ExplorationFilter[] {
  const shown: ExplorationFilter[] = [];
  fullGroups
    .filter((group) => group.key !== 'other')
    .forEach((group) => {
      const count = visibleCounts.get(group.key) ?? 0;
      group.tags.slice(0, count).forEach((tag) => shown.push(tag.filter));
    });
  return shown;
}

function isFilterInList(filter: ExplorationFilter, list: ExplorationFilter[]): boolean {
  return list.some((candidate) => explorationFiltersEqual(candidate, filter));
}

function hiddenFiltersFromVisibleCounts(
  filters: ExplorationFilter[],
  fullGroups: FilterButtonGroup[],
  visibleCounts: Map<string, number>
): ExplorationFilter[] {
  const shown = visibleFiltersFromCounts(fullGroups, visibleCounts);
  return filters.filter((filter) => !isFilterInList(filter, shown));
}

function computeFittedRowWidth(
  input: {
    triggerWidth: number;
    fullGroups: FilterButtonGroup[];
    visibleCounts: Map<string, number>;
    filters: ExplorationFilter[];
    groupPartialWidths: Record<string, number[]>;
    otherChipWidthForCount: (count: number) => number;
    gapPx: number;
  }
): number {
  const {
    triggerWidth,
    fullGroups,
    visibleCounts,
    filters,
    groupPartialWidths,
    otherChipWidthForCount,
    gapPx,
  } = input;

  let width = triggerWidth;
  for (const groupKey of primaryGroupOrder(fullGroups)) {
    const count = visibleCounts.get(groupKey) ?? 0;
    if (count <= 0) continue;
    width += gapPx + (groupPartialWidths[groupKey]?.[count - 1] ?? 0);
  }

  const hiddenCount = hiddenFiltersFromVisibleCounts(filters, fullGroups, visibleCounts).length;
  if (hiddenCount > 0) {
    width += gapPx + otherChipWidthForCount(hiddenCount);
  }

  return width;
}

function buildGroupsFromVisibleCounts(
  fullGroups: FilterButtonGroup[],
  filters: ExplorationFilter[],
  visibleCounts: Map<string, number>
): FilterButtonGroup[] {
  const groups: FilterButtonGroup[] = [];

  fullGroups
    .filter((group) => group.key !== 'other')
    .forEach((group) => {
      const count = visibleCounts.get(group.key) ?? 0;
      if (count <= 0) return;
      groups.push({
        key: group.key,
        label: group.label,
        tags: group.tags.slice(0, count),
      });
    });

  const hidden = hiddenFiltersFromVisibleCounts(filters, fullGroups, visibleCounts);
  const overflow = otherGroup(hidden);
  return overflow ? [...groups, overflow] : groups;
}

/** Keep leading tags that fit; fold overflow into a trailing Other +N chip. */
export function fitFilterButtonGroups(input: FitFilterButtonGroupsInput): FilterButtonGroup[] {
  const {
    fullGroups,
    filters,
    triggerWidth,
    groupPartialWidths,
    otherChipWidthForCount,
    availableWidth,
    gapPx = filterButtonRowGapPx,
  } = input;

  if (!fullGroups.length) return [];
  if (availableWidth <= 0) return fullGroups.filter((group) => group.key !== 'other');

  const flatTags = flattenPrimaryGroupTags(fullGroups);
  if (!flatTags.length) return fullGroups;

  const visibleCounts = new Map<string, number>();

  for (const item of flatTags) {
    const currentCount = visibleCounts.get(item.groupKey) ?? 0;
    if (item.tagIndexInGroup !== currentCount) continue;

    const nextCounts = new Map(visibleCounts);
    nextCounts.set(item.groupKey, currentCount + 1);

    const totalWidth = computeFittedRowWidth({
      triggerWidth,
      fullGroups,
      visibleCounts: nextCounts,
      filters,
      groupPartialWidths,
      otherChipWidthForCount,
      gapPx,
    });

    if (totalWidth <= availableWidth) {
      visibleCounts.set(item.groupKey, currentCount + 1);
    }
  }

  const fitted = buildGroupsFromVisibleCounts(fullGroups, filters, visibleCounts);
  const hiddenCount = hiddenFiltersFromVisibleCounts(filters, fullGroups, visibleCounts).length;

  traceExplorationFilterButton('fit groups to width', {
    availableWidth,
    triggerWidth,
    visibleTagCounts: Object.fromEntries(visibleCounts),
    hiddenCount,
    fittedGroups: fitted.map((group) => ({
      label: group.label,
      tags: group.tags.map((tag) => tag.label),
    })),
  });

  if (fitted.length > 0) return fitted;

  const overflow = otherGroup(filters);
  return overflow ? [overflow] : fullGroups;
}

const NODE_GROUP_MAX_VISIBLE = 2;
const LONG_VALUE_MAX_VISIBLE = 2;

const PRIMARY_GROUP_ORDER: Array<{
  type: ExplorationFilter['type'];
  key: string;
  label: string;
  maxVisible?: number;
  truncate?: boolean;
}> = [
  { type: 'supportStatus', key: 'supportStatus', label: 'Eligibility status' },
  { type: 'activationStatus', key: 'activationStatus', label: 'Activation' },
  { type: 'infrastructure', key: 'infrastructure', label: 'Attached To' },
  { type: 'cluster', key: 'cluster', label: 'Cluster', truncate: true },
  { type: 'region', key: 'region', label: 'Region' },
  { type: 'nodeGroup', key: 'nodeGroup', label: 'Node-group', maxVisible: NODE_GROUP_MAX_VISIBLE, truncate: true },
  { type: 'diskType', key: 'diskType', label: 'Volume Type' },
  { type: 'capacity', key: 'capacity', label: 'Capacity' },
  {
    type: 'volumeId',
    key: 'volumeId',
    label: 'Volume ID',
    maxVisible: LONG_VALUE_MAX_VISIBLE,
    truncate: true,
  },
  {
    type: 'instanceId',
    key: 'instanceId',
    label: 'Instance ID',
    maxVisible: LONG_VALUE_MAX_VISIBLE,
    truncate: true,
  },
  { type: 'tag', key: 'tag', label: 'Tags', maxVisible: LONG_VALUE_MAX_VISIBLE, truncate: true },
  { type: 'accountId', key: 'accountId', label: 'Account', truncate: true },
];

function supportStatusLabel(value: string): string {
  return resolveSupportLabel(value);
}

function filterValueLabel(filter: ExplorationFilter, bounds: CapacityBounds): string {
  switch (filter.type) {
    case 'supportStatus':
      return supportStatusLabel(filter.value);
    case 'activationStatus':
      return ACTIVATION_LABELS[filter.value as keyof typeof ACTIVATION_LABELS] || filter.value;
    case 'infrastructure':
      return (
        INFRASTRUCTURE_LABELS[filter.value as keyof typeof INFRASTRUCTURE_LABELS] || filter.value
      );
    case 'region':
    case 'nodeGroup':
    case 'cluster':
    case 'diskType':
    case 'accountId':
      return filter.value;
    case 'instanceId':
    case 'volumeId':
      return filter.value;
    case 'tag':
      return `${filter.key}:${filter.value}`;
    case 'capacity':
      return formatCapacityRangeBadge(filter.min, filter.max, bounds) ?? 'Capacity';
  }
}

/** Human-readable label for a filter chip (shared by grouping and overflow tooltips). */
export function explorationFilterButtonLabel(
  filter: ExplorationFilter,
  bounds: CapacityBounds
): string {
  return filterValueLabel(filter, bounds);
}

function filtersOfType(filters: ExplorationFilter[], type: ExplorationFilter['type']): ExplorationFilter[] {
  return filters.filter((f) => f.type === type);
}

function otherGroup(hidden: ExplorationFilter[]): FilterButtonGroup | null {
  if (!hidden.length) return null;
  return {
    key: 'other',
    label: 'Other',
    tags: [
      {
        filter: hidden[0]!,
        label: `+${hidden.length}`,
      },
    ],
  };
}

/** Build grouped filter tags for the canvas Filters button (Figma product 2 buttons). */
export function buildFilterButtonGroups(
  filters: ExplorationFilter[],
  bounds: CapacityBounds
): FilterButtonGroup[] {
  if (!filters.length) return [];

  const groups: FilterButtonGroup[] = [];
  const shown = new Set<ExplorationFilter>();
  const hidden: ExplorationFilter[] = [];

  for (const spec of PRIMARY_GROUP_ORDER) {
    const typeFilters = filtersOfType(filters, spec.type);
    if (!typeFilters.length) continue;

    const maxVisible = spec.maxVisible ?? typeFilters.length;
    const visible = typeFilters.slice(0, maxVisible);
    const overflow = typeFilters.slice(maxVisible);

    groups.push({
      key: spec.key,
      label: spec.label,
      tags: visible.map((filter) => ({
        filter,
        label: filterValueLabel(filter, bounds),
        truncate: spec.truncate,
      })),
    });

    visible.forEach((f) => shown.add(f));
    hidden.push(...overflow);
  }

  filters.forEach((filter) => {
    if (shown.has(filter)) return;
    if (PRIMARY_GROUP_ORDER.some((spec) => spec.type === filter.type)) return;
    hidden.push(filter);
  });

  if (hidden.length > 0) {
    groups.push({
      key: 'other',
      label: 'Other',
      tags: [
        {
          filter: hidden[0]!,
          label: `+${hidden.length}`,
        },
      ],
    });
  }

  traceExplorationFilterButton('build groups (full)', {
    filterCount: filters.length,
    filterTypes: filters.map((f) => f.type),
    groups: groups.map((g) => ({
      label: g.label,
      tags: g.tags.map((t) => t.label),
    })),
    hiddenCount: hidden.length,
  });

  return groups;
}

export function hiddenFiltersFromGroups(
  filters: ExplorationFilter[],
  groups: FilterButtonGroup[]
): ExplorationFilter[] {
  const overflowGroup = groups.find((group) => group.key === 'other');
  if (!overflowGroup) return [];

  const shownTags = groups
    .filter((group) => group.key !== 'other')
    .flatMap((group) => group.tags);

  return filters.filter(
    (filter) => !shownTags.some((tag) => explorationFiltersEqual(tag.filter, filter))
  );
}
